import i18n from 'i18next';

/**
 * Client-side upload guards against scriptable SVG and other disallowed types.
 * Extension checks alone are insufficient (e.g. evil.svg renamed to .png).
 */
export const SVG_UPLOAD_BLOCKED_DESCRIPTION =
  i18n.t('common.svg-files-not-allowed');

export type FileValidationResult =
  | { ok: true }
  | { ok: false; message: string };

export const PROFILE_RASTER_IMAGE_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp';

export const TRANSACTION_ATTACHMENT_ACCEPT =
  '.pdf,.doc,.docx,.jpg,.jpeg,.png';

export const ORGANISATION_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,application/pdf';

export const RECEIPT_UPLOAD_ACCEPT =
  '.bmp,.jpeg,.jpg,.gif,.png,.pdf,application/pdf';

/** Includes image/* so mobile OS pickers offer Camera, Gallery, Drive, etc. */
export const RECEIPT_UPLOAD_ACCEPT_WITH_CAMERA =
  `image/*,${RECEIPT_UPLOAD_ACCEPT}`;

export const RECEIPT_FILE_EXTENSIONS = [
  '.bmp',
  '.jpeg',
  '.jpg',
  '.gif',
  '.png',
  '.pdf',
] as const;

const RASTER_IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
] as const;

const RASTER_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

const TRANSACTION_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
] as const;

const ORGANISATION_ATTACHMENT_EXTENSIONS = [
  ...RASTER_IMAGE_EXTENSIONS,
  '.pdf',
] as const;

const ORGANISATION_ATTACHMENT_MIME_TYPES = [
  ...RASTER_IMAGE_MIME_TYPES,
  'application/pdf',
] as const;

export function isSvgUpload(file: File): boolean {
  const name = (file.name ?? '').toLowerCase().trim();
  if (name.endsWith('.svg') || name.endsWith('.svgz')) {
    return true;
  }
  const type = (file.type ?? '').toLowerCase().trim();
  return type === 'image/svg+xml' || type.endsWith('+svg+xml');
}

function fileNameEndsWith(name: string, extensions: readonly string[]): boolean {
  return extensions.some((ext) => name.endsWith(ext));
}

function rejectSvg(file: File): FileValidationResult | null {
  if (isSvgUpload(file)) {
    return { ok: false, message: SVG_UPLOAD_BLOCKED_DESCRIPTION };
  }
  return null;
}

export function validateRasterProfileImage(file: File): FileValidationResult {
  const svgReject = rejectSvg(file);
  if (svgReject) return svgReject;

  const name = (file.name ?? '').toLowerCase();
  const type = (file.type ?? '').toLowerCase();
  const extOk = fileNameEndsWith(name, RASTER_IMAGE_EXTENSIONS);
  const mimeOk =
    !type || (RASTER_IMAGE_MIME_TYPES as readonly string[]).includes(type);

  if (extOk && mimeOk) return { ok: true };
  if (extOk || mimeOk) return { ok: true };

  return {
    ok: false,
    message: i18n.t('common.only-jpeg-png-gif-and-webp-images-allowed'),
  };
}

export function validateTransactionAttachment(
  file: File
): FileValidationResult {
  const svgReject = rejectSvg(file);
  if (svgReject) return svgReject;

  const name = (file.name ?? '').toLowerCase();
  if (fileNameEndsWith(name, TRANSACTION_ATTACHMENT_EXTENSIONS)) {
    return { ok: true };
  }

  return {
    ok: false,
    message: i18n.t('common.only-pdf-word-jpeg-and-png-files-can-be-uploaded'),
  };
}

export function validateOrganisationAttachment(
  file: File
): FileValidationResult {
  const svgReject = rejectSvg(file);
  if (svgReject) return svgReject;

  const name = (file.name ?? '').toLowerCase();
  const type = (file.type ?? '').toLowerCase();
  const extOk = fileNameEndsWith(name, ORGANISATION_ATTACHMENT_EXTENSIONS);
  const mimeOk =
    !type ||
    (ORGANISATION_ATTACHMENT_MIME_TYPES as readonly string[]).includes(type);

  if (extOk && mimeOk) return { ok: true };
  if (extOk || mimeOk) return { ok: true };

  return {
    ok: false,
    message: i18n.t('common.only-jpeg-png-gif-webp-and-pdf-files-can-be-uploaded'),
  };
}

export function validateReceiptUploadFile(file: File): FileValidationResult {
  const svgReject = rejectSvg(file);
  if (svgReject) return svgReject;

  const name = (file.name ?? '').toLowerCase();
  if (fileNameEndsWith(name, RECEIPT_FILE_EXTENSIONS)) {
    return { ok: true };
  }

  return {
    ok: false,
    message: i18n.t('common.only-bmp-jpeg-jpg-gif-png-and-pdf-files-can-be-uploaded'),
  };
}

export function partitionFilesByValidation(
  files: File[],
  validate: (file: File) => FileValidationResult
): { allowed: File[]; rejected: File[] } {
  return files.reduce<{ allowed: File[]; rejected: File[] }>(
    (acc, file) => {
      const result = validate(file);
      if (result.ok) {
        acc.allowed.push(file);
      } else {
        acc.rejected.push(file);
      }
      return acc;
    },
    { allowed: [], rejected: [] }
  );
}
